import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/api/auth';
import { extractApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/components/Toast';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input } from '@/components/Form';

// windows-ux-edit (2026-06, 슬라이드2) — 사용자 자가 비밀번호 변경 모달.
// 변경 성공 시 서버가 전 세션(refresh token)을 폐기하므로, 현재 세션도
// 로그아웃 처리하고 로그인 화면으로 보내 새 비밀번호로 재인증하게 한다.
export function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const toast = useToast();

  async function submit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (next.length < 8) {
      setError('새 비밀번호는 8자 이상이어야 합니다.');
      return;
    }
    if (next !== confirm) {
      setError('새 비밀번호 확인이 일치하지 않습니다.');
      return;
    }
    if (next === current) {
      setError('새 비밀번호가 현재 비밀번호와 동일합니다.');
      return;
    }
    setSubmitting(true);
    try {
      await authApi.changePassword(current, next);
      toast.success(
        '비밀번호가 변경되었습니다. 보안을 위해 다시 로그인해 주세요.',
        '비밀번호 변경',
      );
      clear();
      navigate('/login', { replace: true });
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="비밀번호 변경"
      width={420}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" form="change-password-form" disabled={submitting}>
            {submitting ? '변경 중…' : '변경'}
          </Button>
        </>
      }
    >
      <form id="change-password-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>
        <Field label="현재 비밀번호" htmlFor="cp-current">
          <Input
            id="cp-current"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            autoFocus
          />
        </Field>
        <Field label="새 비밀번호" htmlFor="cp-new" hint="8자 이상">
          <Input
            id="cp-new"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
          />
        </Field>
        <Field label="새 비밀번호 확인" htmlFor="cp-confirm">
          <Input
            id="cp-confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
        </Field>
      </form>
    </Modal>
  );
}
